import API from "@/api";
import { showGlobalLoading } from "@/components/globalLoading";
import { TransactionParams } from "@/components/transcationPopup";
import { AppDispatch, RootState } from "@/redux/store";
import { isDef } from "@/util/validate";
import { Toast, Dialog } from "antd-mobile";
import { isEmpty, isString } from "lodash";
import { useMetaMask } from "@onyx-p/metamask-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useCustomTranslation from "./useTranslation";
import { PayChannel } from "@/pages/Game/type";
import { getUserInfoMethod } from "@/redux/reducers/authSlice";
import useRouter from "./useRouter";

type PayOrderType = {
	address: string;
	network: string;
	currency: string;
	expTime: number;
};
const useDeposit = (type: 1 | 2, isInit: boolean = true) => {
	const dispatch: AppDispatch = useDispatch();
	const { params: routerParams } = useRouter();
	const { account, connect } = useMetaMask();
	const { loginState, userInfo, isAdminType } = useSelector(
		(state: RootState) => state.auth
	);
	const payChannel = useRef<PayChannel | null>();
	const [payChannelData, setPayChannelData] = useState<PayChannel[]>([]);
	const [depositReady, setDepositReady] = useState(false);
	const [contractBalance, setContractBalance] = useState<number>(0);
	const [visible, setVisible] = useState(false);
	const [payOrder, setPayOrder] = useState<PayOrderType | null>(null);

	const { showLoading, hideLoading } = showGlobalLoading();
	const { t } = useCustomTranslation();

	const prepareDeposit = async () => {
		try {
			showLoading();
			const connectedAddr = await prepareNetwork();
			hideLoading();
			if (!isDef(payChannel)) throw new Error(t("PayChannelEmtpy"));
			if (connectedAddr.toLowerCase() !== getUseAccount().toLowerCase()) {
				// 账号钱包地址不匹配
				const confirm = await Dialog.confirm({
					content: t("LoginAccountNotMatchWalletAddr"),
					cancelText: t("Cancel"),
					confirmText: t("Confirm"),
				});
				if (!confirm) {
					throw new Error(t("UserRejected"));
				}
			}
			setDepositReady(true);
		} catch (error: any) {
			hideLoading();
			console.error("preDeposit", error);
			const errorMsg = error?.message ?? error;
			if (isString(errorMsg) && !isEmpty(errorMsg)) {
				Toast.show({
					content: errorMsg as string,
					position: "top",
				});
			}
		}
	};

	const submitDeposit = async () => {
		try {
			const network = payChannel.current?.network;
			if (!isDef(network)) throw new Error(t("AppConfigIsEmpty"));

			showLoading();
			const orderResult: any = await API.finances.apiFinancePayOrder({
				code: payChannel.current?.code,
				protocol: payChannel.current?.protocol,
				itemType: routerParams.type || 1,
			});
			if (orderResult?.code === 0) {
				setPayOrder(orderResult.content.data);
			} else {
				throw new Error(t("RequestOrderFailed"));
			}
			setDepositReady(false);
			console.log("handleDepositSuccess");
		} catch (error: any) {
			let errorMsg = error?.message ?? error;
			if (!isString(errorMsg) || isEmpty(errorMsg)) {
				errorMsg = t("DepositFailed");
			}
			Toast.show({
				content: errorMsg,
				position: "top",
			});
			console.error("handleDepositError", error);
		} finally {
			hideLoading();
		}
	};

	const cancelDeposit = () => {
		setDepositReady(false);
	};

	const loadPayChannel = async () => {
		const response: any = await API.finances.apiPayChannel();
		if (response?.code === 0) {
			const data = response.content.data;
			payChannel.current = data[0];
			setPayChannelData(data);
			submitDeposit();
		}
	};

	const prepareNetwork = async () => {
		setContractBalance(0);

		const network = payChannel.current?.network;
		if (!isDef(network)) return Promise.reject(t("AppConfigIsEmpty"));

		const account = getUseAccount();
		if (!isDef(account)) return Promise.reject(t("UserInfoEmtpy"));

		const connectedAddr = await connectIfNeed();
		if (isEmpty(connectedAddr)) throw new Error(t("WalletEmpty"));

		return connectedAddr!;
	};

	const connectIfNeed = async () => {
		if (!isEmpty(account)) return account!;
		const addrList = await connect();
		return addrList?.[0];
	};

	const getUseAccount = () => {
		if (isAdminType) {
			return userInfo?.income?.walletAddress;
		}
		return userInfo?.walletAddress;
	};

	const depositParams: TransactionParams = {
		min: payChannel.current?.minMoney
			? parseFloat(payChannel.current?.minMoney)
			: null,
		max: payChannel.current?.maxMoney
			? parseFloat(payChannel.current?.maxMoney)
			: null,
		balance: contractBalance,
	};

	const switchPayChannel = (index: number) => {
		if (index >= 0 && index < payChannelData.length) {
			payChannel.current = payChannelData[index];
			submitDeposit();
			setVisible(false);
		} else {
			throw new Error("Invalid index for payChannelData");
		}
	};

	const checkPayChannel = async () => {
		if (!payChannel.current) {
			await loadPayChannel();
		}
	};

	useEffect(() => {
		const fetchData = async () => {
			if (loginState) {
				await dispatch(getUserInfoMethod());
			}
			await loadPayChannel();
		};
		if (isInit) {
			fetchData();
		}
	}, [loginState, isInit]);

	return {
		visible,
		setVisible,
		payOrder,
		payChannel: payChannel.current,
		prepareDeposit,
		submitDeposit,
		cancelDeposit,
		depositReady,
		depositParams,
		payChannelData,
		switchPayChannel,
		checkPayChannel,
	};
};

export default useDeposit;
